import React, { useState } from 'react';
import CategorizedSetsDisplay from './CategorizedSetsDisplay';
import './ModeratorCategorizedSetsView.css';

const ModeratorCategorizedSetsView = ({ 
  sets, 
  setsByCategory,
  poolTitle,
  onViewDetails,
  onSendToCOE,
  loading = false
}) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const categoryConfig = {
    general: { icon: '📘', label: 'General Exam', color: '#0066cc' },
    special: { icon: '📗', label: 'Special Case', color: '#ff9900' },
    reexam: { icon: '📙', label: 'Re-Examination', color: '#cc0000' }
  };

  const handleSendToCOE = () => {
    setShowConfirmModal(true);
  };

  const confirmSendToCOE = async () => {
    setShowConfirmModal(false);
    await onSendToCOE();
  };

  // Check if all 3 categories have sets
  const hasAllCategories = 
    setsByCategory.general?.length > 0 &&
    setsByCategory.special?.length > 0 &&
    setsByCategory.reexam?.length > 0;

  return (
    <div className="moderator-categorized-sets-view">
      {/* Header */}
      <div className="view-header">
        <h2>📦 Categorized Sets Ready for COE</h2>
        <p className="subtitle">{poolTitle}</p>
      </div>

      {/* Status Cards */}
      <div className="status-cards">
        {['general', 'special', 'reexam'].map(category => {
          const config = categoryConfig[category];
          const count = setsByCategory[category]?.length || 0;
          const hasSet = count > 0;

          return (
            <div 
              key={category}
              className={`status-card ${hasSet ? 'complete' : 'incomplete'}`}
              style={{
                borderColor: config.color,
                background: hasSet ? `${config.color}15` : '#f5f5f5'
              }}
            >
              <div className="status-icon" style={{ color: config.color }}>
                {hasSet ? '✓' : '○'}
              </div>
              <div className="status-content">
                <h4 style={{ color: config.color }}>
                  {config.icon} {config.label}
                </h4>
                <p className="status-count">
                  {hasSet ? `Set ${setsByCategory[category][0].set_number}` : 'Not assigned'}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Categorized Sets Display */}
      <div className="sets-display-wrapper">
        <CategorizedSetsDisplay 
          sets={sets}
          setsByCategory={setsByCategory}
          onViewDetails={onViewDetails}
          isModeratorView={true}
        />
      </div>

      {/* Action Buttons */}
      <div className="action-buttons">
        {hasAllCategories ? (
          <>
            <div className="success-message">
              <span className="icon">✓</span>
              <span>All 3 categories assigned! Ready to send to COE.</span>
            </div>
            <button 
              className="btn btn-primary btn-lg"
              onClick={handleSendToCOE}
              disabled={loading}
            >
              {loading ? '⏳ Sending...' : '📤 Send to COE'}
            </button>
          </>
        ) : (
          <div className="warning-message">
            <span className="icon">⚠️</span>
            <span>Please categorize all 3 sets (General, Special, Re-exam) before sending to COE.</span>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>📤 Send to COE</h3>
              <button 
                className="modal-close"
                onClick={() => setShowConfirmModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <p className="modal-message">
                You are about to send the following 3 categorized sets to COE:
              </p>

              <div className="sets-preview">
                {['general', 'special', 'reexam'].map(category => {
                  const config = categoryConfig[category];
                  const set = setsByCategory[category]?.[0];

                  return (
                    <div key={category} className="preview-item">
                      <div className="preview-icon" style={{ color: config.color }}>
                        {config.icon}
                      </div>
                      <div className="preview-content">
                        <h4 style={{ color: config.color }}>{config.label}</h4>
                        <p>Set {set?.set_number || 'N/A'}</p>
                      </div>
                      <div className="preview-check">✓</div>
                    </div>
                  );
                })}
              </div>

              <div className="modal-info">
                <p>
                  <strong>Note:</strong> Once sent to COE, no further changes can be made to these sets. 
                  Any non-categorized sets will be deleted.
                </p>
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowConfirmModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-success"
                onClick={confirmSendToCOE}
                disabled={loading}
              >
                {loading ? '⏳ Sending...' : '✓ Confirm & Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModeratorCategorizedSetsView;
